import React, { useRef, useState } from 'react';
import { Coordinates } from '../types';

interface HeroProps {
  onAnalyze: (input: File | string, allergens: string, illnesses: string, goals: string) => void;
  onCitySubmit: (city: string) => void;
  isAnalyzing: boolean;
  isDarkMode: boolean;
  toggleDarkMode: () => void;
  onRequestLocation: () => void;
  userLocation: Coordinates | null;
  isLocationLoading: boolean;
}

const Hero: React.FC<HeroProps> = ({ 
  onAnalyze, 
  onCitySubmit,
  isAnalyzing, 
  isDarkMode, 
  toggleDarkMode,
  onRequestLocation,
  userLocation,
  isLocationLoading
}) => {
  const [inputMode, setInputMode] = useState<'upload' | 'describe'>('upload');
  const [allergens, setAllergens] = useState('');
  const [illnesses, setIllnesses] = useState('');
  const [goals, setGoals] = useState('');
  const [moodDescription, setMoodDescription] = useState('');
  const [cityInput, setCityInput] = useState('');
  const [preview, setPreview] = useState<string | null>(null);
  const fileInputRef = useRef<HTMLInputElement>(null);

  const handleFileChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (file) {
      const reader = new FileReader();
      reader.onloadend = () => {
        setPreview(reader.result as string);
      };
      reader.readAsDataURL(file);
    }
  };

  const handleManualUploadClick = (e: React.MouseEvent) => {
    e.stopPropagation();
    fileInputRef.current?.click();
  };

  const handleAnalyzeClick = () => {
    if (inputMode === 'upload' && fileInputRef.current?.files?.[0]) {
      onAnalyze(fileInputRef.current.files[0], allergens, illnesses, goals);
    } else if (inputMode === 'describe' && moodDescription.trim()) {
      onAnalyze(moodDescription, allergens, illnesses, goals);
    }
  };

  const handleCitySubmit = (e: React.FormEvent) => {
    e.preventDefault();
    if (cityInput.trim()) {
      onCitySubmit(cityInput.trim());
    }
  };

  const isButtonDisabled = isAnalyzing || (inputMode === 'upload' ? !preview : !moodDescription.trim());

  return (
    <div className="bg-white dark:bg-slate-800 rounded-3xl shadow-xl overflow-hidden mb-8 border border-slate-200 dark:border-slate-700 transition-colors duration-300 relative">
      
      {/* Dark Mode Toggle (Top Right) */}
      <div className="absolute top-6 right-6 z-10">
        <button
          onClick={toggleDarkMode}
          className="p-3 rounded-2xl bg-slate-100 dark:bg-slate-700 text-slate-600 dark:text-slate-300 hover:bg-indigo-600 hover:text-white dark:hover:bg-indigo-500 transition-all duration-300 shadow-sm"
          title={isDarkMode ? "Switch to Light Mode" : "Switch to Dark Mode"}
        >
          {isDarkMode ? (
            <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 3v1m0 16v1m9-9h-1M4 12H3m15.364 6.364l-.707-.707M6.343 6.343l-.707-.707m12.728 0l-.707.707M6.343 17.657l-.707.707M16 12a4 4 0 11-8 0 4 4 0 018 0z" /></svg>
          ) : (
            <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M20.354 15.354A9 9 0 018.646 3.646 9.003 9.003 0 0012 21a9.003 9.003 0 008.354-5.646z" /></svg>
          )}
        </button>
      </div>

      <div className="p-8 md:p-14 text-center">
        <div className="inline-block px-4 py-1.5 mb-6 bg-indigo-100 dark:bg-indigo-900/30 rounded-full">
           <span className="text-xs font-black uppercase tracking-widest text-indigo-600 dark:text-indigo-400">NutriMap AI Assistant</span>
        </div>
        
        <h1 className="text-5xl font-black text-slate-900 dark:text-white mb-6 tracking-tighter leading-tight">
          Snap. Analyze. <span className="text-indigo-600">Discover.</span>
        </h1>
        <p className="text-xl text-slate-500 dark:text-slate-400 mb-12 max-w-2xl mx-auto font-medium">
          Identify dishes from photos, uncover nutritional secrets, and map out exactly where to get them near you.
        </p>

        {/* Input Mode Toggle */}
        <div className="flex justify-center mb-10">
          <div className="inline-flex p-1.5 bg-slate-100 dark:bg-slate-900 rounded-[1.5rem] shadow-inner">
            <button
              onClick={() => setInputMode('upload')}
              className={`px-8 py-3 rounded-[1.2rem] font-black text-sm transition-all duration-300 ${
                inputMode === 'upload' 
                  ? 'bg-white dark:bg-slate-800 text-indigo-600 dark:text-white shadow-lg' 
                  : 'text-slate-500 hover:text-slate-700 dark:hover:text-slate-300'
              }`}
            >
              Upload Photo
            </button>
            <button
              onClick={() => setInputMode('describe')}
              className={`px-8 py-3 rounded-[1.2rem] font-black text-sm transition-all duration-300 ${
                inputMode === 'describe' 
                  ? 'bg-white dark:bg-slate-800 text-indigo-600 dark:text-white shadow-lg' 
                  : 'text-slate-500 hover:text-slate-700 dark:hover:text-slate-300'
              }`}
            >
              Describe Dish
            </button>
          </div>
        </div>

        <div className="max-w-xl mx-auto space-y-8">
          
          {/* Location Request Button & City Search Box */}
          {!userLocation && (
             <div className="space-y-4 mb-8">
                <div className="flex flex-col sm:flex-row items-center justify-center gap-4">
                  <button
                    onClick={onRequestLocation}
                    disabled={isLocationLoading}
                    className="flex items-center gap-2 px-6 py-3 bg-white dark:bg-slate-700 text-slate-700 dark:text-white rounded-2xl text-sm font-black shadow-md hover:bg-indigo-600 hover:text-white transition-all transform active:scale-95 group border border-slate-200 dark:border-slate-600"
                  >
                    {isLocationLoading ? (
                      <span className="animate-spin h-5 w-5 border-2 border-indigo-600 border-t-transparent rounded-full"></span>
                    ) : (
                      <svg className="w-5 h-5 text-indigo-500 group-hover:text-white transition-colors" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={3} d="M17.657 16.657L13.414 20.9a1.998 1.998 0 01-2.827 0l-4.244-4.243a8 8 0 1111.314 0z" /><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={3} d="M15 11a3 3 0 11-6 0 3 3 0 016 0z" /></svg>
                    )}
                    {isLocationLoading ? "Locating..." : "Auto-Locate Me"}
                  </button>

                  <div className="text-slate-400 font-black text-xs uppercase tracking-widest px-2">OR</div>

                  <form onSubmit={handleCitySubmit} className="flex w-full sm:w-auto items-center">
                    <div className="relative flex-grow sm:flex-grow-0">
                      <input 
                        type="text"
                        value={cityInput}
                        onChange={(e) => setCityInput(e.target.value)}
                        placeholder="Closest major city..."
                        className="pl-4 pr-12 py-3 bg-white dark:bg-slate-700 border border-slate-200 dark:border-slate-600 rounded-2xl text-sm font-bold focus:ring-2 focus:ring-indigo-500 focus:border-indigo-500 outline-none w-full sm:w-64"
                      />
                      <button 
                        type="submit"
                        className="absolute right-2 top-1.5 p-1.5 bg-indigo-600 text-white rounded-xl hover:bg-indigo-700 transition-colors shadow-lg shadow-indigo-500/20"
                      >
                        <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={3} d="M13 7l5 5m0 0l-5 5m5-5H6" /></svg>
                      </button>
                    </div>
                  </form>
                </div>
                <p className="text-[10px] text-slate-400 dark:text-slate-500 font-black uppercase tracking-widest">Needed to pinpoint nearby restaurants & ingredients</p>
             </div>
          )}

          {/* Upload Area / Text Area */}
          {inputMode === 'upload' ? (
            <div 
              className={`relative border-4 border-dashed rounded-[2.5rem] p-12 transition-all duration-500 group ${
                preview 
                  ? 'border-indigo-500 bg-indigo-50/50 dark:bg-indigo-900/10' 
                  : 'border-slate-200 dark:border-slate-700 hover:border-indigo-400 bg-slate-50 dark:bg-slate-900/50'
              }`}
            >
              <input
                type="file"
                ref={fileInputRef}
                onChange={handleFileChange}
                accept="image/*"
                className="absolute inset-0 w-full h-full opacity-0 cursor-pointer z-10"
                disabled={isAnalyzing}
              />
              
              {preview ? (
                <div className="relative h-64 w-full">
                  <img 
                    src={preview} 
                    alt="Meal preview" 
                    className="h-full w-full object-cover rounded-[1.5rem] shadow-2xl"
                  />
                  <div className="absolute top-4 right-4 z-20">
                     <button
                      onClick={handleManualUploadClick} 
                      className="bg-white/90 backdrop-blur-md text-slate-900 px-5 py-2 rounded-2xl shadow-xl text-xs font-black uppercase tracking-wider hover:bg-white transition-all transform active:scale-95"
                     >
                       Change
                     </button>
                  </div>
                </div>
              ) : (
                <div className="flex flex-col items-center justify-center text-slate-400 group-hover:text-indigo-500 transition-colors">
                  <div className="w-20 h-20 bg-indigo-100 dark:bg-indigo-900/30 rounded-3xl flex items-center justify-center mb-6 shadow-inner group-hover:scale-110 group-hover:rotate-6 transition-all duration-500">
                    <svg className="w-10 h-10 text-indigo-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2.5} d="M4 16l4.586-4.586a2 2 0 012.828 0L16 16m-2-2l1.586-1.586a2 2 0 012.828 0L20 14m-6-6h.01M6 20h12a2 2 0 002-2V6a2 2 0 00-2-2H6a2 2 0 00-2 2v12a2 2 0 002 2z" />
                    </svg>
                  </div>
                  <span className="font-black text-sm uppercase tracking-[0.2em] mb-4">Snap a meal photo</span>
                  <button 
                    type="button"
                    onClick={handleManualUploadClick}
                    className="relative z-20 px-8 py-3.5 bg-indigo-600 text-white rounded-2xl shadow-xl shadow-indigo-600/20 text-sm font-black hover:bg-indigo-700 transition-all transform hover:scale-105 active:scale-95"
                  >
                    Select From Library
                  </button>
                </div>
              )}
            </div>
          ) : (
            <div>
              <textarea
                value={moodDescription}
                onChange={(e) => setMoodDescription(e.target.value)}
                placeholder="Hungry? Describe it. e.g. 'A protein-packed breakfast that's keto-friendly' or 'I want something spicy with noodles'"
                className="w-full h-56 px-6 py-6 rounded-[2rem] border-2 border-slate-200 dark:border-slate-700 bg-white dark:bg-slate-900 text-slate-900 dark:text-white focus:ring-4 focus:ring-indigo-500/20 focus:border-indigo-500 outline-none transition-all resize-none shadow-inner text-lg font-medium"
                disabled={isAnalyzing}
              />
            </div>
          )}

          <div className="grid grid-cols-1 md:grid-cols-3 gap-4 text-left">
            {/* Allergens */}
            <div>
              <label htmlFor="allergens" className="block text-[10px] font-black text-slate-400 dark:text-slate-500 uppercase tracking-widest mb-2 pl-2">
                Allergies
              </label>
              <input
                type="text"
                id="allergens"
                value={allergens}
                onChange={(e) => setAllergens(e.target.value)}
                placeholder="Peanuts, etc."
                className="w-full px-5 py-3.5 rounded-2xl border-2 border-slate-100 dark:border-slate-700 bg-white dark:bg-slate-700 text-slate-900 dark:text-white focus:ring-2 focus:ring-indigo-500 focus:border-indigo-500 outline-none transition-all font-bold"
                disabled={isAnalyzing}
              />
            </div>

            {/* Illnesses */}
            <div className="md:col-span-1">
              <label htmlFor="illnesses" className="block text-[10px] font-black text-slate-400 dark:text-slate-500 uppercase tracking-widest mb-2 pl-2">
                Conditions
              </label>
              <input
                type="text"
                id="illnesses"
                value={illnesses}
                onChange={(e) => setIllnesses(e.target.value)}
                placeholder="Diabetes, etc."
                className="w-full px-5 py-3.5 rounded-2xl border-2 border-slate-100 dark:border-slate-700 bg-white dark:bg-slate-700 text-slate-900 dark:text-white focus:ring-2 focus:ring-indigo-500 focus:border-indigo-500 outline-none transition-all font-bold"
                disabled={isAnalyzing}
              />
            </div>

            {/* Goals */}
            <div>
              <label htmlFor="goals" className="block text-[10px] font-black text-slate-400 dark:text-slate-500 uppercase tracking-widest mb-2 pl-2">
                Dietary Goals
              </label>
              <input
                type="text"
                id="goals"
                value={goals}
                onChange={(e) => setGoals(e.target.value)}
                placeholder="Muscle gain, etc."
                className="w-full px-5 py-3.5 rounded-2xl border-2 border-slate-100 dark:border-slate-700 bg-white dark:bg-slate-700 text-slate-900 dark:text-white focus:ring-2 focus:ring-indigo-500 focus:border-indigo-500 outline-none transition-all font-bold"
                disabled={isAnalyzing}
              />
            </div>
          </div>

          {/* Action Button */}
          <button
            onClick={handleAnalyzeClick}
            disabled={isButtonDisabled}
            className={`w-full py-6 rounded-[2rem] font-black text-xl text-white shadow-2xl transform transition-all flex justify-center items-center gap-3 active:scale-95 ${
              isButtonDisabled
                ? 'bg-slate-300 dark:bg-slate-700 cursor-not-allowed text-slate-500 shadow-none'
                : 'bg-gradient-to-r from-indigo-600 via-violet-600 to-indigo-600 hover:scale-[1.02] shadow-indigo-500/30'
            }`}
          >
            {isAnalyzing ? (
              <>
                <svg className="animate-spin h-6 w-6 text-white" viewBox="0 0 24 24"><circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle><path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path></svg>
                Processing Plate...
              </>
            ) : (
              'Analyze & Discover'
            )}
          </button>
        </div>
      </div>
    </div>
  );
};

export default Hero;